import { FunctionComponent, memo, useContext, useEffect, useMemo, useState } from "react";
import { IProps } from "./phrase-trigger-sample-interaction-preview.types";
import Modal from "@edgetier/modal";
import InteractionModalContent from "components-for/interactions/interaction-modal-content";
import InteractionPreviews from "components-for/interactions/interaction-modal/interaction-previews";
import { NumericValue } from "@edgetier/client-components";
import { IPhraseTriggerSample } from "@edgetier/watchtower-types";
import { useModalKeyboardShortcut } from "hooks-for/modal/use-modal-keyboard-shortcut";
import { getSurroundingElements } from "utilities/get-surrounding-elements";
import { InteractionContext } from "components-for/interactions/interaction-context/interaction-context";
import "./phrase-trigger-sample-interaction-preview.scss";

/**
 * Display a modal with the interaction details belonging to a phrase sample
 * @param props.phraseSample
 * @param props.phraseSamples
 * @param props.modalProps
 */
const PhraseTriggerSampleInteractionPreview: FunctionComponent<IProps> = ({
    phraseSample,
    phraseSamples,
    ...modalProps
}) => {
    const [visiblePhraseSample, setVisiblePhraseSample] = useState<IPhraseTriggerSample>(phraseSample);
    const { areWritingIssuesVisible } = useContext(InteractionContext);

    const [phraseSamplesBefore, phraseSamplesAfter] = useMemo(() => {
        return getSurroundingElements<IPhraseTriggerSample>(phraseSamples.indexOf(visiblePhraseSample), phraseSamples);
    }, [visiblePhraseSample, phraseSamples]);

    useEffect(() => {
        setVisiblePhraseSample(phraseSample);
    }, [phraseSample]);

    useModalKeyboardShortcut<IPhraseTriggerSample>(phraseSamples, visiblePhraseSample, setVisiblePhraseSample);

    return (
        <Modal size="super-wide" {...modalProps}>
            <InteractionPreviews<IPhraseTriggerSample>
                previewItems={phraseSamplesBefore}
                onPreviewClick={setVisiblePhraseSample}
                position="before"
            >
                {(phraseTriggerSample) => (
                    <div className="phrase-sample-preview__text">
                        <div className="phrase-sample-preview__summary">
                            <div className="phrase-sample-preview__summary__threshold">
                                <NumericValue
                                    value={phraseTriggerSample.score * 100}
                                    options={{
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }}
                                    unit="%"
                                />
                            </div>
                            <div className="phrase-sample-preview__summary__sentence">
                                {phraseTriggerSample.sentence}
                            </div>
                        </div>
                    </div>
                )}
            </InteractionPreviews>
            <InteractionModalContent
                interactionId={visiblePhraseSample.interactionId}
                areWritingIssuesVisible={areWritingIssuesVisible}
                highlightedMessageId={visiblePhraseSample.interactionMessageId}
            />
            <InteractionPreviews<IPhraseTriggerSample>
                previewItems={phraseSamplesAfter}
                onPreviewClick={setVisiblePhraseSample}
                position="after"
            >
                {(phraseTriggerSample) => (
                    <div className="phrase-sample-preview__text">
                        <div className="phrase-sample-preview__summary">
                            <div className="phrase-sample-preview__summary__threshold">
                                <NumericValue
                                    value={phraseTriggerSample.score * 100}
                                    options={{
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }}
                                    unit="%"
                                />
                            </div>
                            <div className="phrase-sample-preview__summary__sentence">
                                {phraseTriggerSample.sentence}
                            </div>
                        </div>
                    </div>
                )}
            </InteractionPreviews>
        </Modal>
    );
};

export default memo(PhraseTriggerSampleInteractionPreview);
